import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Alert } from 'react-bootstrap';
import useChatAgent, { sumEstimatedCostUsd } from './useChatAgent';
import ChatThread from './ChatThread';
import ChatComposer from './ChatComposer';
import ChatLanguageMenu, { LANGUAGE_QUESTION_ID } from './ChatLanguageMenu';
import BookingProposalGrid from '../IngestWorkflow/BookingProposalGrid';
import { useIngestController } from '../IngestWorkflow/IngestController';
import useIngestSession from '../IngestWorkflow/useIngestSession';
import useChatDraftSession from '../IngestWorkflow/useChatDraftSession';
import { ASK_AI_EVENT } from './AskAIButton';
import AskAiLauncher from './AskAiLauncher';
import { GLOBAL_SUGGESTIONS } from './askAiSuggestions';

// Three-state UI: compact card by default, can expand to a larger card,
// or be minimised to a pill. Persisted so the operator's choice survives
// reloads.
const MODE_STORAGE_KEY = 'chatWidget:mode';
const VALID_MODES = ['closed', 'compact', 'expanded'];
const DEFAULT_MODE = 'compact';

const STATUS_LABELS = {
  connecting: 'Connecting…',
  connected: 'Online',
  error: 'Connection failed',
  disconnected: 'Disconnected',
};

// Matches the fullscreen breakpoint in App.scss (.chat-widget-card).
const MOBILE_MEDIA_QUERY = '(max-width: 575.98px)';

function isMobileViewport() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
}

function readStoredMode() {
  if (typeof window === 'undefined') return DEFAULT_MODE;
  const raw = window.localStorage.getItem(MODE_STORAGE_KEY);
  if (raw && VALID_MODES.includes(raw)) return raw;
  // First-time mobile users shouldn't get the card auto-covering the
  // dashboard — fall back to the pill on small screens only.
  if (isMobileViewport()) return 'closed';
  return DEFAULT_MODE;
}

function ChatWidget() {
  const [mode, setMode] = useState(readStoredMode);
  // The draft text, the textarea's auto-grow and the typewriter placeholder
  // all live in <ChatComposer> now — three identical copies of them used to
  // live here, one per render path below.
  // Imperative handle on the thread so we can force-scroll on send /
  // on mode change. Auto-scroll-during-streaming is owned by the
  // thread itself; we only reach in for the explicit operator-action
  // moments where "near bottom" heuristics shouldn't apply.
  const threadRef = useRef(null);

  // Cross-component handle on the offer→bookings workflow. When
  // `activeOfferId` is set (operator clicked "Build bookings" on an
  // offer), the widget overrides its normal compact/expanded modes
  // and renders a near-fullscreen overlay with chat on the left and
  // the editable proposal grid on the right.
  const { activeOfferId, endIngest } = useIngestController();
  const inIngestMode = Boolean(activeOfferId);

  const isOpen = mode !== 'closed' || inIngestMode;
  const isExpanded = mode === 'expanded';

  // No `storageNamespace`: the floating widget keeps the original, un-suffixed
  // localStorage keys, so the conversation already sitting in an operator's
  // browser survives. Every OTHER mounted chat passes one — see ChatPanel.
  const {
    blocks, sessionId, status, error, pending, send, answer, decideApproval, reset,
  } = useChatAgent();

  // Forward the imperative scroll handle so the ingest hook's
  // "Ingest all" / "Discuss changes" can snap-to-bottom on click.
  const scrollToLatest = useCallback((behavior = 'auto') => {
    threadRef.current?.scrollToLatest(behavior);
  }, []);

  // All ingest-specific state: page_key, edited rows, kickoff effect,
  // commit results, reconciliation. Inert when activeOfferId is null.
  // sessionId is passed in so the kickoff effect can wait for reset()
  // to commit a fresh session before sending the kickoff (otherwise
  // it routes to the pre-reset session).
  const ingest = useIngestSession({
    activeOfferId,
    blocks,
    sessionId,
    status,
    pending,
    send,
    reset,
    scrollToLatest,
  });

  // ── Chat-initiated draft mode ──
  //
  // When the agent emits a `bookings-proposal` block during a normal
  // chat conversation (operator pasted an email / customer request and
  // asked to draft bookings), we surface the same ingest overlay with
  // the editable grid — without requiring the operator to navigate to
  // the Service Offers page first.
  //
  // `dismissedProposalId` tracks which proposal block the operator
  // dismissed so closing the overlay doesn't re-open it for the same
  // proposal, but a fresh proposal from a revision turn does re-open it.
  const [dismissedProposalId, setDismissedProposalId] = useState(null);

  const latestChatProposalBlock = useMemo(() => {
    // Only active in regular chat mode (not while an offer ingest is running,
    // which has its own dedicated session).
    if (inIngestMode) return null;
    for (let i = blocks.length - 1; i >= 0; i -= 1) {
      const b = blocks[i];
      if (b && b.kind === 'proposal' && b.proposal) return b;
    }
    return null;
  }, [blocks, inIngestMode]);

  const hasChatProposal = Boolean(latestChatProposalBlock);
  const chatDraftDismissed = latestChatProposalBlock?.id === dismissedProposalId;
  const inChatDraftMode = hasChatProposal && !chatDraftDismissed;

  // Clear stale dismissed state when all proposal blocks are gone (chat reset).
  useEffect(() => {
    if (!hasChatProposal) setDismissedProposalId(null);
  }, [hasChatProposal]);

  const chatDraft = useChatDraftSession({
    active: inChatDraftMode,
    blocks,
    pending,
    send,
    scrollToLatest,
  });

  // Persist mode across reloads.
  useEffect(() => {
    window.localStorage.setItem(MODE_STORAGE_KEY, mode);
  }, [mode]);

  // Lock background scroll while the card is full-bleed on mobile —
  // otherwise tapping/dragging inside the card lets the dashboard scroll
  // behind it. iOS-safe: save scrollY, fix the body, restore on unlock.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_MEDIA_QUERY);
    const { body } = document;
    let locked = false;
    let savedScrollY = 0;

    const lock = () => {
      if (locked) return;
      savedScrollY = window.scrollY;
      body.style.position = 'fixed';
      body.style.top = `-${savedScrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
      locked = true;
    };

    const unlock = () => {
      if (!locked) return;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      window.scrollTo(0, savedScrollY);
      locked = false;
    };

    const sync = () => {
      if (isOpen && mq.matches) lock();
      else unlock();
    };

    sync();
    // Safari <14 only supports addListener.
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else mq.addListener(sync);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', sync);
      else mq.removeListener(sync);
      unlock();
    };
  }, [isOpen]);

  // Opening (or switching compact↔expanded) → snap to latest. Auto-
  // scroll-during-streaming is owned by ChatThread; this effect only
  // fires on the discrete mode-change moments.
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => threadRef.current?.scrollToLatest('auto'));
    }
  }, [isOpen, mode]);

  const composerDisabled = status !== 'connected' || pending;

  const handleSubmit = (text) => {
    // Sending = user intent to see what comes back; force the thread
    // to the bottom regardless of where the scroll was, so the new
    // turn is in view as the response streams.
    threadRef.current?.scrollToLatest('auto');
    send(text);
  };

  // "Ask AI" from a screen (AskAIButton): open expanded on a fresh chat and
  // send its question as the first message. The question waits for reset()
  // to commit the new session — `send` closes over the session id, so sending
  // in the same pass would land it in the conversation just thrown away (the
  // same wait as the ingest kickoff in useIngestSession). `raised` lifts the
  // card over an open modal, since the question may come from inside Order
  // Details; minimising drops it again.
  const [asked, setAsked] = useState(null);
  const [raised, setRaised] = useState(false);
  useEffect(() => {
    const onAsk = (event) => {
      setMode('expanded');
      setRaised(true);
      reset();
      setAsked({ text: event.detail.text, staleSession: sessionId });
    };
    window.addEventListener(ASK_AI_EVENT, onAsk);
    return () => window.removeEventListener(ASK_AI_EVENT, onAsk);
  }, [reset, sessionId]);
  useEffect(() => {
    if (!asked || sessionId === asked.staleSession) return;
    setAsked(null);
    handleSubmit(asked.text);
  }, [asked, sessionId, send]);

  // Reply-language change: sent as an answer to the same question the
  // agent asks once on its own, so the server's single validated write
  // path handles it. Not dashboard localisation — only the language the
  // assistant replies in.
  const handleLanguage = useCallback((code) => {
    answer(LANGUAGE_QUESTION_ID, code);
  }, [answer]);

  const statusLabel = STATUS_LABELS[status] || '';

  // Running tally next to "New chat". Hidden when zero (e.g. server has no
  // pricing for the model id).
  const totalCostUsd = useMemo(() => sumEstimatedCostUsd(blocks), [blocks]);

  // Closing in ingest mode: drop the active offer too. Operator
  // returns to the offers page in the background (their previous
  // viewport). The widget's own mode (compact/expanded) is preserved
  // so reopening Ask AI afterwards lands them where they left it.
  //
  // We also pre-dismiss the latest proposal block (if any) so the chat
  // draft overlay doesn't immediately re-open when the offer ingest
  // ends and `inIngestMode` flips back to false — the offer session's
  // blocks (which may contain proposal blocks) stay in the chat thread.
  const handleClose = useCallback(() => {
    if (inIngestMode) {
      const lastProposal = [...blocks].reverse().find((b) => b?.kind === 'proposal' && b.proposal);
      if (lastProposal) setDismissedProposalId(lastProposal.id);
      endIngest();
      return;
    }
    if (inChatDraftMode) {
      setDismissedProposalId(latestChatProposalBlock?.id ?? null);
      return;
    }
    setMode('closed');
  }, [inIngestMode, inChatDraftMode, endIngest, blocks, latestChatProposalBlock]);

  if (inIngestMode) {
    // ── Ingest overlay (offer→bookings workflow) ───────────────────────
    return (
      <div className="chat-widget-ingest-backdrop" role="presentation">
        <div
          className="chat-widget-ingest-card"
          role="dialog"
          aria-label="Build bookings from service offer"
        >
          <div className="chat-widget-card-header">
            <div className="chat-widget-header-content">
              <div className="chat-widget-header-title">
                <i className="fa fa-magic me-2 text-primary" aria-hidden="true" />
                Build bookings from offer
              </div>
              <div className="chat-widget-header-meta">
                <span
                  className={`chat-widget-status-dot chat-widget-status-dot-${status}`}
                  aria-hidden="true"
                />
                <span>{statusLabel}</span>
                <span className="chat-widget-header-sep" aria-hidden="true">·</span>
                <span className="font-monospace small text-muted">{activeOfferId}</span>
                {sessionId && (
                  <>
                    <span className="chat-widget-header-sep" aria-hidden="true">·</span>
                    <button
                      type="button"
                      className="chat-widget-header-new"
                      onClick={reset}
                      title="Restart the agent conversation"
                    >
                      <i className="fa fa-refresh" aria-hidden="true" />
                      <span>Restart chat</span>
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="chat-widget-card-actions">
              <button
                type="button"
                className="chat-widget-card-icon"
                onClick={handleClose}
                aria-label="Close ingest workflow"
                title="Close"
              >
                <i className="fa fa-times" aria-hidden="true" />
              </button>
            </div>
          </div>

          {error && (
            <Alert
              variant="danger"
              className="mb-0 mx-3 mt-3 d-flex align-items-center gap-2"
            >
              <i className="fa fa-triangle-exclamation" aria-hidden="true" />
              <div>
                <strong>Agent error:</strong>
                {' '}
                {error}
              </div>
            </Alert>
          )}

          {ingest.reconciliationLoaded && ingest.alreadyIngestedBookings.length > 0 && (
            <Alert
              variant="info"
              className="mb-0 mx-3 mt-3 d-flex align-items-center gap-2"
              data-testid="reconciliation-banner"
            >
              <i className="fa fa-info-circle" aria-hidden="true" />
              <div>
                <strong>
                  {`${ingest.alreadyIngestedBookings.length} booking${ingest.alreadyIngestedBookings.length === 1 ? '' : 's'} already ingested from this offer.`}
                </strong>
                {' '}
                Re-running Ingest all is safe — rows that already landed
                will short-circuit via the server-side dedup, not double-create.
              </div>
            </Alert>
          )}

          <div className="chat-widget-ingest-body">
            <div className="chat-widget-ingest-chat">
              <ChatThread
                ref={threadRef}
                blocks={blocks}
                pending={pending}
                error={null}
                status={status}
                onAnswer={answer}
                onDecideApproval={decideApproval}
                placeholder={
                  status === 'connecting'
                    ? 'Connecting to the agent…'
                    : 'Starting the workflow — drafting bookings from the offer.'
                }
              />
              <ChatComposer
                onSubmit={handleSubmit}
                disabled={composerDisabled}
                pending={pending}
                rows={2}
                placeholder="Ask the agent to adjust the proposal…"
              />
            </div>
            <div className="chat-widget-ingest-grid">
              <BookingProposalGrid
                proposal={ingest.proposal}
                rows={ingest.rows}
                onRowEdit={ingest.onRowEdit}
                onIngestAll={ingest.onIngestAll}
                onDiscussChanges={ingest.onDiscussChanges}
                hasEdits={ingest.hasEdits}
                commitResult={ingest.commitResult}
                fieldWarnings={ingest.fieldWarnings}
                ingesting={pending}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (inChatDraftMode) {
    // ── Chat-initiated draft overlay (text → bookings workflow) ────────
    return (
      <div className="chat-widget-ingest-backdrop" role="presentation">
        <div
          className="chat-widget-ingest-card"
          role="dialog"
          aria-label="Draft bookings from chat"
        >
          <div className="chat-widget-card-header">
            <div className="chat-widget-header-content">
              <div className="chat-widget-header-title">
                <i className="fa fa-magic me-2 text-primary" aria-hidden="true" />
                Draft bookings from chat
              </div>
              <div className="chat-widget-header-meta">
                <span
                  className={`chat-widget-status-dot chat-widget-status-dot-${status}`}
                  aria-hidden="true"
                />
                <span>{statusLabel}</span>
                {sessionId && (
                  <>
                    <span className="chat-widget-header-sep" aria-hidden="true">·</span>
                    <button
                      type="button"
                      className="chat-widget-header-new"
                      onClick={reset}
                      title="Start a new conversation"
                    >
                      <i className="fa fa-refresh" aria-hidden="true" />
                      <span>New chat</span>
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="chat-widget-card-actions">
              <button
                type="button"
                className="chat-widget-card-icon"
                onClick={handleClose}
                aria-label="Close draft overlay"
                title="Close"
              >
                <i className="fa fa-times" aria-hidden="true" />
              </button>
            </div>
          </div>

          {error && (
            <Alert
              variant="danger"
              className="mb-0 mx-3 mt-3 d-flex align-items-center gap-2"
            >
              <i className="fa fa-triangle-exclamation" aria-hidden="true" />
              <div>
                <strong>Agent error:</strong>
                {' '}
                {error}
              </div>
            </Alert>
          )}

          <div className="chat-widget-ingest-body">
            <div className="chat-widget-ingest-chat">
              <ChatThread
                ref={threadRef}
                blocks={blocks}
                pending={pending}
                error={null}
                status={status}
                onAnswer={answer}
                onDecideApproval={decideApproval}
                placeholder="Ask the agent to adjust the proposal…"
              />
              <ChatComposer
                onSubmit={handleSubmit}
                disabled={composerDisabled}
                pending={pending}
                rows={2}
                placeholder="Ask the agent to adjust the proposal…"
              />
            </div>
            <div className="chat-widget-ingest-grid">
              <BookingProposalGrid
                proposal={chatDraft.proposal}
                rows={chatDraft.rows}
                onRowEdit={chatDraft.onRowEdit}
                onIngestAll={chatDraft.onIngestAll}
                onDiscussChanges={chatDraft.onDiscussChanges}
                hasEdits={chatDraft.hasEdits}
                commitResult={chatDraft.commitResult}
                fieldWarnings={chatDraft.fieldWarnings}
                ingesting={pending}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isOpen) {
    return <AskAiLauncher onOpen={() => setMode('compact')} />;
  }

  return (
    <div
      className={`chat-widget-card chat-widget-card-${isExpanded ? 'expanded' : 'compact'}${raised ? ' chat-widget-card-raised' : ''}`}
      role="dialog"
      aria-label="Pickup Hungary Assistant"
    >
      <div className="chat-widget-card-header">
        <div className="chat-widget-header-content">
          <div className="chat-widget-header-title">Pickup Hungary Assistant</div>
          <div className="chat-widget-header-meta">
            <span
              className={`chat-widget-status-dot chat-widget-status-dot-${status}`}
              aria-hidden="true"
            />
            <span>{statusLabel}</span>
            {sessionId && (
              <>
                <span className="chat-widget-header-sep" aria-hidden="true">·</span>
                <button
                  type="button"
                  className="chat-widget-header-new"
                  onClick={reset}
                  title="Start a new conversation"
                >
                  <i className="fa fa-plus" aria-hidden="true" />
                  <span>New chat</span>
                </button>
                <span className="chat-widget-header-sep" aria-hidden="true">·</span>
                <ChatLanguageMenu onChoose={handleLanguage} disabled={pending} />
                {totalCostUsd > 0 && (
                  <>
                    <span className="chat-widget-header-sep" aria-hidden="true">·</span>
                    <span
                      className="chat-widget-header-cost"
                      title={`Estimated cost this session: ~$${totalCostUsd.toFixed(6)} USD`}
                    >
                      {`$${totalCostUsd.toFixed(4)}`}
                    </span>
                  </>
                )}
              </>
            )}
          </div>
        </div>
        <div className="chat-widget-card-actions">
          <button
            type="button"
            className="chat-widget-card-icon"
            onClick={() => setMode(isExpanded ? 'compact' : 'expanded')}
            aria-label={isExpanded ? 'Shrink' : 'Expand'}
            title={isExpanded ? 'Shrink' : 'Expand'}
          >
            <i
              className={`fa ${isExpanded ? 'fa-compress' : 'fa-expand'}`}
              aria-hidden="true"
            />
          </button>
          <button
            type="button"
            className="chat-widget-card-icon"
            onClick={() => { setRaised(false); setMode('closed'); }}
            aria-label="Minimise"
            title="Minimise"
          >
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        </div>
      </div>

      <ChatThread
        ref={threadRef}
        blocks={blocks}
        pending={pending}
        error={error}
        status={status}
        onAnswer={answer}
        onDecideApproval={decideApproval}
        placeholder="Ask me about pricing, bookings, flights, or anything else the dashboard surfaces."
      />

      <ChatComposer
        onSubmit={handleSubmit}
        disabled={composerDisabled}
        pending={pending}
        placeholder="Ask the assistant…"
        examples={GLOBAL_SUGGESTIONS}
        resizeKey={mode}
      />
    </div>
  );
}

export default ChatWidget;
